import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import MediaPlaceholder from '../../../components/MediaPlaceholder';
import MediaImageItem from '../../../components/MediaImageItem';
import type { Annotation } from '../../../features/annotation';
import useImageFrame from '../../../hooks/useImageFrame';
import InspectionHistoryPanel from './InspectionHistoryPanel';
import type { InspectionMediaData } from '../inspectionMediaTypes';
import { shouldHideAnnotationHistoryPanel } from '../inspectionTabs';
import { features } from '../../../config/platform';
import { inspectionApi } from '../inspectionAPI';
import { imageHasAnnotationHistory } from '../utils/annotationHistoryUtils';

interface InspectionImageContentProps {
  inspectionId: string;
  imageData: InspectionMediaData;
  annotations: Annotation[];
  setAnnotations: React.Dispatch<React.SetStateAction<Annotation[]>>;
  onClose: () => void;
  downloadFileName?: string;
  downloadAsset?: (url: string, fileName?: string) => Promise<void>;
  refreshAfterAnnotationChange: () => Promise<void> | void;
  disableAnnotation?: boolean;
  activeTab?: string;
  onCreateSuccess?: () => void;
  onImageResolved?: (info: { imageFileId: string; imageURI?: string }) => void;
}

const InspectionImageContent: React.FC<InspectionImageContentProps> = ({
  inspectionId,
  imageData,
  annotations,
  setAnnotations,
  onClose,
  downloadFileName,
  downloadAsset,
  refreshAfterAnnotationChange,
  disableAnnotation = false,
  activeTab,
  onCreateSuccess,
  onImageResolved,
}) => {
  const imageSrc = imageData.imageURI ?? '';
  const title = imageData.title ?? '';
  const { imageFrame, hasMeasuredFrame, handleImageLoad } = useImageFrame(imageSrc);

  // The measuring <img> gates the whole annotation view (we render the canvas
  // only once we have the frame's dimensions). Without error handling a failed
  // load leaves the placeholder spinning forever.
  const [imageLoadError, setImageLoadError] = useState(false);
  const [measureRetryToken, setMeasureRetryToken] = useState(0);

  useEffect(() => {
    setImageLoadError(false);
  }, [imageSrc]);

  const handleMeasureRetry = useCallback(() => {
    setImageLoadError(false);
    setMeasureRetryToken(token => token + 1);
  }, []);

  const { data: damages = [] } = inspectionApi.useGetDamagesQuery(inspectionId, {
    skip: !inspectionId,
  });

  // Use the raw damages payload (including deleted) so history still shows
  // after every active annotation on this image has been removed.
  const hasHistory = useMemo(() => imageHasAnnotationHistory(damages, imageData), [damages, imageData]);
  const hideHistoryPanel = shouldHideAnnotationHistoryPanel(activeTab, hasHistory);
  const showImageCloseButton = !hasHistory || hideHistoryPanel;
  return (
    <div className="flex h-full w-full items-stretch box-border">
      <div className="relative flex min-w-0 flex-1 items-center justify-center">
        {showImageCloseButton && (
          <button
            type="button"
            aria-label="Close media"
            onClick={onClose}
            className="absolute right-3 top-3 z-30 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-[11px] text-white transition hover:bg-black/70">
            <CloseOutlined />
          </button>
        )}
        {!imageSrc ? (
          <MediaPlaceholder />
        ) : imageLoadError ? (
          <div className="flex flex-col items-center gap-3 text-white/80">
            <span className="text-sm">Failed to load image.</span>
            <button
              type="button"
              onClick={handleMeasureRetry}
              className="rounded-md bg-white/15 px-4 py-1.5 text-sm text-white transition hover:bg-white/25">
              Retry
            </button>
          </div>
        ) : !hasMeasuredFrame ? (
          <>
            <div className="absolute inset-0">
              <MediaPlaceholder />
            </div>
            <img
              key={measureRetryToken}
              // Cloud signed cookies need credentialed CORS; on-prem uses the URL as-is.
              {...(features.isCloudPortal ? { crossOrigin: 'use-credentials' as const } : {})}
              onLoad={handleImageLoad}
              onError={() => setImageLoadError(true)}
              src={imageSrc}
              alt={imageData.title}
              className="pointer-events-none max-h-full max-w-full rounded-md object-contain opacity-0"
            />
          </>
        ) : (
          <MediaImageItem
            item={{ ...imageData, imageURI: imageSrc, title }}
            imageFrame={imageFrame}
            inspectionId={inspectionId}
            annotations={annotations}
            setAnnotations={setAnnotations}
            onItemClick={() => {}}
            viewName={title}
            downloadFileName={downloadFileName}
            downloadAsset={downloadAsset}
            onAnnotationChanged={refreshAfterAnnotationChange}
            disableAnnotation={disableAnnotation}
            activeTab={activeTab}
            onCreateSuccess={onCreateSuccess}
            onImageResolved={onImageResolved}
          />
        )}
      </div>
      {hasHistory && !hideHistoryPanel && <InspectionHistoryPanel inspectionId={inspectionId} imageData={imageData} onClose={onClose} />}
    </div>
  );
};

export default InspectionImageContent;
